import React from "react";
import { HtmlContent } from "@/components/RichText";

/**
 * Big Five (trait model) result blocks, driven by `result.big_five` from the backend
 * big_five resolver: a standout-trait badge for the hero card and a trait map where each
 * trait is a spectrum between its two poles.
 */

const ACCENT = "#D47A65";
const BAND_LABELS = {
  low: { id: "Rendah", en: "Low" },
  mid: { id: "Sedang", en: "Moderate" },
  high: { id: "Tinggi", en: "High" },
};

const pick = (source, field, lang) => {
  const own = source?.[`${field}_${lang === "en" ? "en" : "id"}`];
  const other = source?.[`${field}_${lang === "en" ? "id" : "en"}`];
  return own || other || "";
};

/** Label shown for a trait's band: the admin's range label when set, else Low/Moderate/High. */
export function bigFiveBandLabel(trait, lang = "id") {
  return pick(trait?.category, "label", lang) || BAND_LABELS[trait?.band]?.[lang === "en" ? "en" : "id"] || "";
}

/** "Extraversion · High" or the pole name ("Extravert") when the admin set one. */
export function bigFiveHighlightLabel(highlight, lang = "id") {
  if (!highlight) return "";
  const name = pick(highlight, "name", lang) || highlight.code || "";
  const pole = pick(highlight, "pole_name", lang);
  if (pole) return `${pole} (${name})`;
  const level = BAND_LABELS[highlight.pole === "HIGH" ? "high" : "low"][lang === "en" ? "en" : "id"];
  return `${name} · ${level}`;
}

export function BigFiveHighlightBadge({ bigFive, lang = "id", color }) {
  if (!bigFive) return null;
  const tint = color || ACCENT;
  const highlights = bigFive.highlights || [];
  const balanced = bigFive.profile === "BALANCED" || highlights.length === 0;
  return (
    <div className="flex flex-col items-center gap-1.5 text-center" data-testid="big-five-highlight-badge">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {balanced
          ? (lang === "en" ? "Your profile" : "Profilmu")
          : highlights.length > 1
            ? (lang === "en" ? "Your standout traits" : "Trait paling menonjol")
            : (lang === "en" ? "Your standout trait" : "Trait paling menonjol")}
      </span>
      <div className="flex flex-wrap justify-center gap-1.5">
        {balanced ? (
          <span className="px-3 py-1.5 rounded-full text-sm font-display" style={{ background: tint, color: "#fff" }}>
            {lang === "en" ? "Balanced across all traits" : "Seimbang di semua trait"}
          </span>
        ) : highlights.map((h, i) => (
          <span
            key={h.result_key}
            className="px-3 py-1.5 rounded-full text-sm font-display"
            style={i === 0 ? { background: tint, color: "#fff" } : { background: tint + "22", color: tint }}
          >
            {bigFiveHighlightLabel(h, lang)}
          </span>
        ))}
      </div>
    </div>
  );
}

function TraitSpectrum({ trait, lang, highlighted }) {
  const pct = Math.max(0, Math.min(100, Number(trait.percentage) || 0));
  const name = pick(trait, "name", lang) || trait.code;
  const lowPole = pick(trait, "pole_low", lang) || BAND_LABELS.low[lang === "en" ? "en" : "id"];
  const highPole = pick(trait, "pole_high", lang) || BAND_LABELS.high[lang === "en" ? "en" : "id"];
  const interpretation = pick(trait.category, "interpretation", lang);
  const recommendation = pick(trait.category, "recommendation", lang);
  const description = pick(trait, "description", lang);
  return (
    <div
      className={`p-4 md:p-5 rounded-2xl border ${highlighted ? "border-terracotta-300 bg-terracotta-50/40" : "border-border bg-card"}`}
      data-testid={`big-five-trait-${trait.code}`}
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-display text-lg text-teal-800 break-words">{name}</div>
          {description && <div className="text-xs text-muted-foreground break-words"><HtmlContent html={description} /></div>}
        </div>
        <div className="flex items-baseline gap-2 shrink-0">
          <span className="text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full bg-terracotta-100 text-terracotta-700" data-testid={`big-five-band-${trait.code}`}>
            {bigFiveBandLabel(trait, lang)}
          </span>
          <span className="font-display text-xl tabular-nums text-teal-800">{Math.round(pct)}%</span>
        </div>
      </div>
      <div className="mt-4">
        <div
          className="relative h-2.5 rounded-full"
          style={{ background: "linear-gradient(90deg, rgb(38 77 79 / 0.18), rgb(38 77 79 / 0.06) 50%, rgb(212 122 101 / 0.28))" }}
          role="img"
          aria-label={`${name}: ${Math.round(pct)}% (${lowPole} – ${highPole})`}
        >
          <div className="absolute top-1/2 left-1/2 h-4 w-px -translate-y-1/2 bg-teal-800/25" aria-hidden />
          <div
            className="absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white shadow"
            style={{ left: `${pct}%`, background: ACCENT }}
            data-testid={`big-five-marker-${trait.code}`}
          />
        </div>
        <div className="mt-2 flex justify-between gap-3 text-[11px] text-muted-foreground">
          <span className="break-words">{lowPole}</span>
          <span className="break-words text-right">{highPole}</span>
        </div>
      </div>
      {interpretation && (
        <div className="mt-3 text-sm text-teal-800/90 leading-relaxed"><HtmlContent html={interpretation} /></div>
      )}
      {recommendation && (
        <div className="mt-2 text-xs text-muted-foreground italic"><HtmlContent html={recommendation} /></div>
      )}
    </div>
  );
}

export function BigFiveProfileSection({ bigFive, lang = "id" }) {
  const traits = bigFive?.traits || [];
  if (!traits.length) return null;
  const highlighted = new Set((bigFive.highlights || []).map((h) => h.dimension_id));
  return (
    <section className="mb-8 p-5 md:p-6 rounded-3xl bg-card border border-border" data-testid="big-five-profile-section">
      <div className="mb-4">
        <div className="font-display text-lg md:text-xl">{lang === "en" ? "Your Trait Map" : "Peta Trait Kamu"}</div>
        <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
          {lang === "en"
            ? "Each trait is a spectrum, not a grade. Both ends have their own strengths; the dot shows where you sit today."
            : "Setiap trait adalah spektrum, bukan nilai. Kedua ujungnya punya kekuatan masing-masing; titiknya menunjukkan posisimu saat ini."}
        </p>
      </div>
      <div className="space-y-3">
        {traits.map((trait) => (
          <TraitSpectrum key={trait.dimension_id} trait={trait} lang={lang} highlighted={highlighted.has(trait.dimension_id)} />
        ))}
      </div>
    </section>
  );
}
